import {styled} from '@linaria/react';
import React from 'react';
import {mediaTabletAndUp} from '../../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../constants/typography';
import {Text as BaseText} from '../../../atoms/Text';

export type TextSize = 'xs' | 's' | 'm' | 'l' | 'xl';
export type Alignment = 'left' | 'center' | 'right' | 'justify';

export interface ContentBlockTextProps {
  tag?: 'h1' | 'h2' | 'span';
  text: string;
  color: string;
  alignment: Alignment;
  size?: TextSize;
  testId?: string;
  weight?: keyof typeof FONT_WEIGHTS;
  isAParagraph?: boolean;
}

export const ContentBlockText: React.FC<
  ContentBlockTextProps & {className?: string}
> = ({
  tag = 'span',
  testId = 'contentBlockText',
  className,
  alignment,
  color,
  size = 's',
  text,
  weight,
  isAParagraph,
}) => {
  const textWithLineBreaks = text.split('\n'); // the content coming from CMS sometimes has line breaks

  return (
    <StyledText
      data-testid={testId}
      className={className}
      textAlignment={alignment}
      $color={color}
      $size={size}
      as={tag}
      weight={weight}
      isAParagraph={isAParagraph}
    >
      {textWithLineBreaks.map((line, index) => (
        <React.Fragment key={index}>
          {line} {index + 1 < textWithLineBreaks.length && <br />}
        </React.Fragment>
      ))}
    </StyledText>
  );
};

const fontMap: Record<TextSize, FONT_SIZES> = {
  xs: FONT_SIZES.px12,
  s: FONT_SIZES.px16,
  m: FONT_SIZES.px20,
  l: FONT_SIZES.px24,
  xl: FONT_SIZES.px32,
};

const tabletAndUpfontMap: Record<TextSize, FONT_SIZES> = {
  xs: FONT_SIZES.px12,
  s: FONT_SIZES.px16,
  m: FONT_SIZES.px24,
  l: FONT_SIZES.px40,
  xl: FONT_SIZES.px56,
};

const StyledText = styled(BaseText)<{
  $color: string;
  $size: TextSize;
  isAParagraph?: boolean;
}>`
  &&& {
    color: ${({$color}) => $color};
    display: block;
    font-size: ${({$size}) => fontMap[$size]};
    line-height: ${({isAParagraph}) => (isAParagraph ? '1.4' : 'inherit')};

    @media ${mediaTabletAndUp} {
      font-size: ${props => tabletAndUpfontMap[props.$size]};
    }
  }
`;
